// src/hooks/useKnowledgeAtoms.js
// Knowledge Graph — hook para CRUD + filtros + health metrics
// v3 — fixes: verifiedBy dinâmico via useAuth, supabase import, stale closure, mutation feedback
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './useAuth';

const PAGE_SIZE = 50;

export default function useKnowledgeAtoms() {
  const { user } = useAuth();
  const [atoms, setAtoms] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    category: null,
    source_type: null,
    knowledge_class: null,
    confidence_band: null,
    entity_type: null,
    status: 'active',
    page: 0,
  });

  const entityNamesRef = useRef({});
  const [entityNames, setEntityNames] = useState({});

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Health metrics ─────────────────────────────────────────
  const fetchHealth = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('v_knowledge_health')
        .select('*');
      if (err) throw err;
      if (data && data.length > 0) setHealth(data[0]);
    } catch (err) {
      console.error('Health fetch error:', err);
    }
  }, []);

  // ── Atoms com filtros ──────────────────────────────────────
  const fetchAtoms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('knowledge_atoms')
        .select(`
          id, atom_type, category, subcategory, content, structured_data,
          source_type, source_document_id, source_email_id,
          source_excerpt, entity_type, entity_id,
          valid_from, valid_to, reference_date,
          confidence, extraction_method, extraction_model,
          knowledge_class, status, verified_by, verified_at,
          superseded_by, created_by, created_by_agent,
          created_at, updated_at
        `)
        .order('created_at', { ascending: false })
        .range(filters.page * PAGE_SIZE, (filters.page + 1) * PAGE_SIZE - 1);

      if (filters.status) query = query.eq('status', filters.status);
      if (filters.category) query = query.eq('category', filters.category);
      if (filters.source_type) query = query.eq('source_type', filters.source_type);
      if (filters.knowledge_class) query = query.eq('knowledge_class', filters.knowledge_class);
      if (filters.entity_type) query = query.eq('entity_type', filters.entity_type);
      if (filters.search) query = query.ilike('content', `%${filters.search}%`);

      if (filters.confidence_band === 'high_95+') {
        query = query.gte('confidence', 0.95);
      } else if (filters.confidence_band === 'medium_80+') {
        query = query.gte('confidence', 0.80).lt('confidence', 0.95);
      } else if (filters.confidence_band === 'low_60+') {
        query = query.gte('confidence', 0.60).lt('confidence', 0.80);
      } else if (filters.confidence_band === 'very_low') {
        query = query.lt('confidence', 0.60);
      }

      const { data, error: err } = await query;
      if (err) throw err;
      setAtoms(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Atoms fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ── Enriquecer atoms com nomes de entidade ─────────────────
  const enrichEntityNames = useCallback(async (atomList) => {
    const ids = { empresa: new Set(), contato: new Set(), imovel: new Set(), oferta: new Set() };
    const currentNames = entityNamesRef.current;

    atomList.forEach(a => {
      if (a.entity_type && a.entity_id && ids[a.entity_type] && !currentNames[a.entity_id]) {
        ids[a.entity_type].add(a.entity_id);
      }
    });

    const totalNew = Object.values(ids).reduce((sum, s) => sum + s.size, 0);
    if (totalNew === 0) return;

    const newNames = {};
    const fetches = [];

    if (ids.empresa.size > 0) {
      fetches.push(
        supabase.from('empresas').select('id, company_name')
          .in('id', [...ids.empresa])
          .then(({ data }) => (data || []).forEach(e => { newNames[e.id] = e.company_name; }))
      );
    }
    if (ids.contato.size > 0) {
      fetches.push(
        supabase.from('contatos').select('id, first_name, last_name')
          .in('id', [...ids.contato])
          .then(({ data }) => (data || []).forEach(c => { newNames[c.id] = `${c.first_name} ${c.last_name || ''}`.trim(); }))
      );
    }
    if (ids.imovel.size > 0) {
      fetches.push(
        supabase.from('imoveis').select('id, nome')
          .in('id', [...ids.imovel])
          .then(({ data }) => (data || []).forEach(i => { newNames[i.id] = i.nome; }))
      );
    }
    if (ids.oferta.size > 0) {
      fetches.push(
        supabase.from('ofertas').select('id, nome_oferta')
          .in('id', [...ids.oferta])
          .then(({ data }) => (data || []).forEach(o => { newNames[o.id] = o.nome_oferta; }))
      );
    }

    await Promise.all(fetches);
    entityNamesRef.current = { ...currentNames, ...newNames };
    setEntityNames({ ...entityNamesRef.current });
  }, []);

  // ── Mutations ──────────────────────────────────────────────
  const updateAtom = useCallback(async (atomId, updates) => {
    try {
      const { error: err } = await supabase
        .from('knowledge_atoms')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', atomId);
      if (err) throw err;
      setAtoms(prev => prev.map(a => a.id === atomId ? { ...a, ...updates } : a));
      return true;
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
      return false;
    }
  }, [showToast]);

  const archiveAtom = useCallback(async (atomId) => {
    const ok = await updateAtom(atomId, { status: 'archived' });
    if (ok) {
      setAtoms(prev => prev.filter(a => a.id !== atomId));
      showToast('success', 'Átomo arquivado');
      fetchHealth();
    }
  }, [updateAtom, fetchHealth, showToast]);

  const disputeAtom = useCallback(async (atomId) => {
    const ok = await updateAtom(atomId, { status: 'disputed' });
    if (ok) {
      showToast('success', 'Átomo marcado como disputado');
      fetchHealth();
    }
  }, [updateAtom, fetchHealth, showToast]);

  // D3 FIX: verifiedBy usa user autenticado real, não hardcoded
  const verifyAtom = useCallback(async (atomId) => {
    const verifiedBy = user?.email || 'unknown';
    const ok = await updateAtom(atomId, {
      verified_by: verifiedBy,
      verified_at: new Date().toISOString(),
      confidence: 1.0,
    });
    if (ok) {
      showToast('success', `Validado por ${verifiedBy} (100%)`);
      fetchHealth();
    }
  }, [updateAtom, fetchHealth, showToast, user]);

  // ── Lifecycle ──────────────────────────────────────────────
  useEffect(() => { fetchHealth(); }, [fetchHealth]);
  useEffect(() => { fetchAtoms(); }, [fetchAtoms]);
  useEffect(() => {
    if (atoms.length > 0) enrichEntityNames(atoms);
  }, [atoms, enrichEntityNames]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 0 }));
  }, []);

  const nextPage = useCallback(() => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  }, []);

  const prevPage = useCallback(() => {
    setFilters(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }));
  }, []);

  const hasMore = atoms.length === PAGE_SIZE;

  return {
    atoms, health, loading, error, toast,
    filters, setFilter, entityNames,
    nextPage, prevPage, hasMore,
    updateAtom, archiveAtom, disputeAtom, verifyAtom,
    refetch: fetchAtoms,
  };
}
